/**
 * Listing Configuration Manager
 * Tenant admin page to manage list view configurations for each entity
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Settings,
  Eye,
  EyeOff,
  ArrowUp,
  ArrowDown,
  Star,
  Copy,
  List,
  RefreshCw
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const ListingConfigManager = () => {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntity, setSelectedEntity] = useState('Customer');
  const [selectedTab, setSelectedTab] = useState(null);
  const [availableTabs, setAvailableTabs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);

  const availableEntities = [
    { name: 'Customer', label: 'Customers', icon: '👥' },
    { name: 'Lead', label: 'Leads', icon: '🎯' },
    { name: 'Opportunity', label: 'Opportunities', icon: '💼' },
    { name: 'Activity', label: 'Activities', icon: '📅' }
  ];

  useEffect(() => {
    loadConfigs();
  }, [selectedEntity]);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/admin/listing-configs?entityName=${selectedEntity}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to load configurations');

      const data = await response.json();
      setConfigs(data.configs || []);
      
      // Load tab information if entity supports tabs
      await loadTabInfo();
    } catch (error) {
      console.error('Error loading configs:', error);
      toast.error('Failed to load listing configurations');
    } finally {
      setLoading(false);
    }
  };

  const loadTabInfo = async () => {
    try {
      const response = await fetch(
        `/api/admin/listing-configs/${selectedEntity}/default`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.config && data.config.supportsTabs && data.config.tabs) {
          const tabs = Object.entries(data.config.tabs).map(([key, tab]) => ({
            key,
            label: tab.label,
            filter: tab.filter
          }));
          setAvailableTabs(tabs);
          // Reset selected tab when entity changes
          setSelectedTab(null);
        } else {
          setAvailableTabs([]);
          setSelectedTab(null);
        }
      }
    } catch (error) {
      console.error('Error loading tab info:', error);
      setAvailableTabs([]);
      setSelectedTab(null);
    }
  };

  const handleCreateConfig = () => {
    setEditingConfig({
      entityName: selectedEntity,
      configName: '',
      isActive: true,
      isDefault: false,
      configData: {
        pageSize: 20,
        defaultSort: { field: 'CreatedAt', order: 'DESC' },
        enableGrouping: false,
        enableSearch: true,
        enableFilters: true,
        enableExport: true,
        enableColumnResize: true,
        enableColumnReorder: true
      },
      fields: []
    });
    setShowConfigForm(true);
  };

  const handleEditConfig = async (configId, entityName, tabName = null) => {
    try {
      let response;
      
      // If configId is 'global', use the default endpoint with entityName
      if (configId === 'global' && entityName) {
        const url = tabName 
          ? `/api/admin/listing-configs/${entityName}/default?tab=${tabName}`
          : `/api/admin/listing-configs/${entityName}/default`;
        response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      } else {
        // For regular config IDs, use the specific config endpoint
        response = await fetch(`/api/admin/listing-configs/${configId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      if (!response.ok) throw new Error('Failed to load configuration details');

      const data = await response.json();

      // Normalize payload to camelCase structure expected by the editor
      const cfg = data.config || {};
      const normalized = {
        ConfigID: configId, // Preserve the config ID for proper update detection
        entityName: cfg.entityName || cfg.EntityName || entityName || selectedEntity,
        configName: cfg.configName || cfg.ConfigName || '',
        isActive: typeof cfg.isActive === 'boolean' ? cfg.isActive : (cfg.IsActive !== undefined ? cfg.IsActive : true),
        isDefault: typeof cfg.isDefault === 'boolean' ? cfg.isDefault : (cfg.IsDefault !== undefined ? cfg.IsDefault : false),
        supportsTabs: cfg.supportsTabs || false,
        tabs: cfg.tabs || null,
        tabInfo: cfg.tabInfo || null,
        selectedTab: tabName,
        configData: cfg.configData || cfg.ConfigData || {
          pageSize: 20,
          defaultSort: { field: 'CreatedAt', order: 'DESC' },
          enableSearch: true,
          enableFilters: true,
          enableExport: true,
          enableColumnResize: true,
          enableColumnReorder: true
        },
        fields: (cfg.fields || []).map(f => ({
          // Accept both camelCase (global JSON) and PascalCase (DB)
          fieldId: f.fieldId || f.FieldID,
          fieldName: f.fieldName || f.FieldName || '',
          displayLabel: f.displayLabel || f.DisplayLabel || '',
          isVisible: typeof f.isVisible === 'boolean' ? f.isVisible : (f.IsVisible !== undefined ? f.IsVisible : true),
          displayOrder: f.displayOrder !== undefined ? f.displayOrder : (f.DisplayOrder !== undefined ? f.DisplayOrder : 0),
          width: f.width || f.Width || 'auto',
          alignment: f.alignment || f.Alignment || 'left',
          formatType: f.formatType || f.FormatType || 'text',
          formatOptions: f.formatOptions || f.FormatOptions || null,
          conditionalDisplay: f.conditionalDisplay || f.ConditionalDisplay || null,
          customRenderer: f.customRenderer || f.CustomRenderer || null,
          isSortable: typeof f.isSortable === 'boolean' ? f.isSortable : (f.IsSortable !== undefined ? f.IsSortable : true),
          isFilterable: typeof f.isFilterable === 'boolean' ? f.isFilterable : (f.IsFilterable !== undefined ? f.IsFilterable : true),
          isSearchable: typeof f.isSearchable === 'boolean' ? f.isSearchable : (f.IsSearchable !== undefined ? f.IsSearchable : true)
        })),
        // Mark if this came from global source so save routes to global API
        isGlobal: data.source === 'global' || configId === 'global'
      };

      setEditingConfig(normalized);
      setShowConfigForm(true);
    } catch (error) {
      console.error('Error loading config details:', error);
      toast.error('Failed to load configuration details');
    }
  };

  const handleDeleteConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/listing-configs/${configId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete configuration');
      }

      toast.success('Configuration deleted successfully');
      loadConfigs();
    } catch (error) {
      console.error('Error deleting config:', error);
      toast.error(error.message || 'Failed to delete configuration');
    }
  };

  const handleInitializeDefault = async (entityName) => {
    try {
      const response = await fetch(
        `/api/admin/listing-configs/initialize/${entityName}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to initialize configuration');
      }

      toast.success(`Default configuration initialized for ${entityName}`);
      loadConfigs();
    } catch (error) {
      console.error('Error initializing config:', error);
      toast.error(error.message || 'Failed to initialize configuration');
    }
  };

  const handleSaveConfig = async (configData) => {
    try {
      const isNew = !configData.ConfigID;
      let url, method, body;

      if (configData.isGlobal) {
        // Save to global listing config API
        url = `/api/admin/global-listing-configs/${encodeURIComponent(configData.entityName)}`;
        method = 'PUT';
        body = JSON.stringify({
          configName: configData.configName,
          isActive: configData.isActive,
          isDefault: configData.isDefault,
          configData: configData.configData,
          fields: configData.fields,
          supportsTabs: configData.supportsTabs,
          tabs: configData.tabs,
          selectedTab: configData.selectedTab
        });
      } else {
        // Tenant route
        url = isNew
          ? '/api/admin/listing-configs'
          : `/api/admin/listing-configs/${configData.ConfigID}`;
        method = isNew ? 'POST' : 'PUT';
        body = JSON.stringify({
          entityName: configData.entityName,
          configName: configData.configName,
          isActive: configData.isActive,
          isDefault: configData.isDefault,
          configData: configData.configData,
          fields: configData.fields,
          supportsTabs: configData.supportsTabs,
          tabs: configData.tabs,
          selectedTab: configData.selectedTab
        });
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save configuration');
      }

      toast.success(`Configuration ${isNew ? 'created' : 'updated'} successfully`);
      setShowConfigForm(false);
      setEditingConfig(null);
      loadConfigs();
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(error.message || 'Failed to save configuration');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            List Configuration Manager
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Customize how entity lists are displayed for your tenant
          </p>
        </div>
        <button
          onClick={handleCreateConfig}
          className="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2"
        >
          <Plus className="h-4 w-4" />
          <span>New Configuration</span>
        </button>
      </div>

      {/* Entity Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Select Entity
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {availableEntities.map((entity) => (
            <button
              key={entity.name}
              onClick={() => setSelectedEntity(entity.name)}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedEntity === entity.name
                  ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              <div className="text-3xl mb-2">{entity.icon}</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white">
                {entity.label}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Selector (only show if entity supports tabs) */}
      {availableTabs.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Tab Configuration
          </label>
          <div className="flex space-x-3">
            <button
              onClick={() => setSelectedTab(null)}
              className={`px-4 py-2 rounded-lg border-2 transition-all ${
                selectedTab === null
                  ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
              }`}
            >
              <div className="text-sm font-medium">General Configuration</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">Default fields for all tabs</div>
            </button>
            {availableTabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setSelectedTab(tab.key)}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  selectedTab === tab.key
                    ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                }`}
              >
                <div className="text-sm font-medium">{tab.label}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Tab-specific fields</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Configurations List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
              <List className="h-5 w-5" />
              <span>{selectedEntity} Configurations</span>
            </h2>
            <button
              onClick={() => handleInitializeDefault(selectedEntity)}
              className="text-sm bg-green-600 dark:bg-green-500 text-white px-3 py-1.5 rounded hover:bg-green-700 dark:hover:bg-green-600 flex items-center space-x-1"
            >
              <RefreshCw className="h-3 w-3" />
              <span>Initialize Default</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-300">Loading configurations...</p>
          </div>
        ) : configs.length === 0 ? (
          <div className="p-12 text-center">
            <Settings className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              No configurations found for {selectedEntity}
            </p>
            <button
              onClick={() => handleInitializeDefault(selectedEntity)}
              className="bg-blue-600 dark:bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600"
            >
              Initialize Default Configuration
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {configs.map((config) => (
              <div
                key={config.ConfigID}
                className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {config.ConfigName}
                      </h3>
                      {config.IsDefault && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                          <Star className="h-3 w-3 mr-1" />
                          Default
                        </span>
                      )}
                      {!config.IsActive && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                      <p>
                        <span className="font-medium">Fields:</span> {config.FieldCount} configured
                      </p>
                      <p>
                        <span className="font-medium">Page Size:</span> {config.ConfigData?.pageSize || 20}
                      </p>
                      <p>
                        <span className="font-medium">Sort By:</span>{' '}
                        {config.ConfigData?.defaultSort?.field || 'None'}{' '}
                        ({config.ConfigData?.defaultSort?.order || 'ASC'})
                      </p>
                      {config.CreatedByName && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                          Created by {config.CreatedByName} on{' '}
                          {new Date(config.CreatedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEditConfig(config.ConfigID, config.EntityName, selectedTab)}
                      className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg"
                      title="Edit"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteConfig(config.ConfigID)}
                      className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                      title="Delete"
                      disabled={config.IsDefault && configs.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Config Form Modal */}
      {showConfigForm && editingConfig && (
        <ConfigFormModal
          config={editingConfig}
          onSave={handleSaveConfig}
          onCancel={() => {
            setShowConfigForm(false);
            setEditingConfig(null);
          }}
        />
      )}
    </div>
  );
};

// Config Form Modal Component
const ConfigFormModal = ({ config, onSave, onCancel }) => {
  const [formData, setFormData] = useState(config);
  const [activeTab, setActiveTab] = useState('general');
  const [availableFields, setAvailableFields] = useState({});
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [showFieldPicker, setShowFieldPicker] = useState(false);
  const [selectedFieldNames, setSelectedFieldNames] = useState(new Set());
  const [fieldSearch, setFieldSearch] = useState('');

  useEffect(() => {
    const loadFields = async () => {
      try {
        setFieldsLoading(true);
        const res = await fetch(`/api/entity-config/${encodeURIComponent(formData.entityName)}/fields`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!res.ok) throw new Error('Failed to load fields');
        const data = await res.json();
        setAvailableFields(data.fields || {});
      } catch (err) {
        console.error('Error loading entity fields:', err);
        setAvailableFields({});
      } finally {
        setFieldsLoading(false);
      }
    };
    loadFields();
  }, [formData.entityName]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleFieldChange = (index, field, value) => {
    const newFields = [...formData.fields];
    newFields[index] = { ...newFields[index], [field]: value };
    setFormData({ ...formData, fields: newFields });
  };

  const handleAddField = () => {
    setSelectedFieldNames(new Set());
    setFieldSearch('');
    setShowFieldPicker(true);
  };

  const toggleFieldSelection = (name) => {
    setSelectedFieldNames(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const confirmAddSelectedFields = () => {
    const names = Array.from(selectedFieldNames);
    const used = new Set(formData.fields.map(f => f.fieldName));
    let order = formData.fields.length;
    const additions = names.filter(n => !used.has(n)).map(n => {
      const base = (availableFields || {})[n] || {};
      return {
        fieldName: n,
        displayLabel: base.label || base.displayName || n,
        isVisible: true,
        displayOrder: order++,
        width: '150px',
        alignment: 'left',
        formatType: 'text',
        isSortable: true,
        isFilterable: true,
        isSearchable: true
      };
    });
    if (additions.length > 0) {
      setFormData({ ...formData, fields: [...formData.fields, ...additions] });
    }
    setShowFieldPicker(false);
    setSelectedFieldNames(new Set());
  };

  const handleRemoveField = (index) => {
    const newFields = formData.fields.filter((_, i) => i !== index);
    setFormData({ ...formData, fields: newFields });
  };

  const moveField = (index, direction) => {
    const newFields = [...formData.fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newFields.length) return;

    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    newFields.forEach((field, i) => field.displayOrder = i);
    setFormData({ ...formData, fields: newFields });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {config.ConfigID ? 'Edit' : 'Create'} Configuration
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tab Selection (if entity supports tabs) */}
        {config.supportsTabs && config.tabs && (
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Configuration Type
            </label>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, selectedTab: null })}
                className={`px-4 py-2 rounded-lg border-2 transition-all ${
                  formData.selectedTab === null
                    ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                }`}
              >
                <div className="text-sm font-medium">General Configuration</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Default fields for all tabs</div>
              </button>
              {Object.entries(config.tabs).map(([key, tab]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setFormData({ ...formData, selectedTab: key })}
                  className={`px-4 py-2 rounded-lg border-2 transition-all ${
                    formData.selectedTab === key
                      ? 'border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                      : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <div className="text-sm font-medium">{tab.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Tab-specific fields</div>
                </button>
              ))}
            </div>
            {formData.selectedTab && (
              <div className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                Editing configuration for: <span className="font-medium">{config.tabs[formData.selectedTab].label}</span>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('general')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'general'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              General Settings
            </button>
            <button
              onClick={() => setActiveTab('fields')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'fields'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              Fields ({formData.fields.length})
            </button>
          </nav>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {activeTab === 'general' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Configuration Name *
                </label>
                <input
                  type="text"
                  value={formData.configName}
                  onChange={(e) => setFormData({ ...formData, configName: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                    className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Set as Default</span>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Page Size
                  </label>
                  <input
                    type="number"
                    value={formData.configData.pageSize}
                    onChange={(e) => setFormData({
                      ...formData,
                      configData: { ...formData.configData, pageSize: parseInt(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Sort Field
                  </label>
                  <input
                    type="text"
                    value={formData.configData.defaultSort?.field || ''}
                    onChange={(e) => setFormData({
                      ...formData,
                      configData: {
                        ...formData.configData,
                        defaultSort: { ...formData.configData.defaultSort, field: e.target.value }
                      }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Feature toggles */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Features
                </label>
                {['enableSearch', 'enableFilters', 'enableExport', 'enableColumnResize', 'enableColumnReorder'].map((feature) => (
                  <label key={feature} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.configData[feature]}
                      onChange={(e) => setFormData({
                        ...formData,
                        configData: { ...formData.configData, [feature]: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {feature.replace('enable', '').replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'fields' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Configure which fields to display and how they should appear
                </p>
                <button
                  type="button"
                  onClick={handleAddField}
                  className="bg-green-600 dark:bg-green-500 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 flex items-center space-x-1 text-sm"
                >
                  <Plus className="h-3 w-3" />
                  <span>Add Field</span>
                </button>
              </div>

              {formData.fields.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No fields configured. Click "Add Field" to start.
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.fields.map((field, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => moveField(index, 'up')}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
                          >
                            <ArrowUp className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveField(index, 'down')}
                            disabled={index === formData.fields.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-30"
                          >
                            <ArrowDown className="h-4 w-4" />
                          </button>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Field {index + 1}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveField(index)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Field Name *
                          </label>
                          <select
                            value={field.fieldName}
                            onChange={(e) => handleFieldChange(index, 'fieldName', e.target.value)}
                            required
                            disabled={fieldsLoading}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                          >
                            <option value="" disabled>{fieldsLoading ? 'Loading fields...' : 'Select a field'}</option>
                            {Object.keys(availableFields || {}).map(name => (
                              <option key={name} value={name}>{name}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Display Label
                          </label>
                          <input
                            type="text"
                            value={field.displayLabel}
                            onChange={(e) => handleFieldChange(index, 'displayLabel', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Format Type
                          </label>
                          <select
                            value={field.formatType}
                            onChange={(e) => handleFieldChange(index, 'formatType', e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                          >
                            <option value="text">Text</option>
                            <option value="email">Email</option>
                            <option value="phone">Phone</option>
                            <option value="url">URL</option>
                            <option value="date">Date</option>
                            <option value="datetime">Date Time</option>
                            <option value="currency">Currency</option>
                            <option value="number">Number</option>
                            <option value="badge">Badge</option>
                            <option value="boolean">Boolean</option>
                            <option value="reference">Reference</option>
                            <option value="custom">Custom</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Width
                          </label>
                          <input
                            type="text"
                            value={field.width}
                            onChange={(e) => handleFieldChange(index, 'width', e.target.value)}
                            placeholder="e.g., 150px, 20%, auto"
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-600 text-gray-900 dark:text-white"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-3 mt-3">
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={field.isVisible}
                            onChange={(e) => handleFieldChange(index, 'isVisible', e.target.checked)}
                            className="h-3 w-3 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300">Visible</span>
                        </label>
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={field.isSortable}
                            onChange={(e) => handleFieldChange(index, 'isSortable', e.target.checked)}
                            className="h-3 w-3 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300">Sortable</span>
                        </label>
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={field.isFilterable}
                            onChange={(e) => handleFieldChange(index, 'isFilterable', e.target.checked)}
                            className="h-3 w-3 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300">Filterable</span>
                        </label>
                        <label className="flex items-center space-x-1">
                          <input
                            type="checkbox"
                            checked={field.isSearchable}
                            onChange={(e) => handleFieldChange(index, 'isSearchable', e.target.checked)}
                            className="h-3 w-3 text-blue-600 dark:text-blue-400 focus:ring-blue-500 dark:focus:ring-blue-400 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <span className="text-xs text-gray-700 dark:text-gray-300">Searchable</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </form>

        {/* Field Picker Modal for adding fields from entity config */}
        <FieldPickerModal
          entityName={formData.entityName}
          availableFields={availableFields}
          usedNames={new Set(formData.fields.map(f => f.fieldName))}
          open={showFieldPicker}
          loading={fieldsLoading}
          search={fieldSearch}
          onSearch={setFieldSearch}
          selectedNames={selectedFieldNames}
          onToggle={toggleFieldSelection}
          onCancel={() => setShowFieldPicker(false)}
          onConfirm={confirmAddSelectedFields}
        />

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>Save Configuration</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Field Picker Modal
const FieldPickerModal = ({ entityName, availableFields = {}, usedNames = new Set(), open, loading, search, onSearch, selectedNames = new Set(), onToggle, onCancel, onConfirm }) => {
  if (!open) return null;
  const all = Object.keys(availableFields || {});
  const filtered = all.filter(n => n.toLowerCase().includes((search || '').toLowerCase()));
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Add Fields from {entityName}</h4>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-6 w-6" />
          </button>
        </div>
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={loading ? 'Loading fields…' : 'Search fields'}
            disabled={loading}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <div className="flex-1 overflow-auto p-4 space-y-2">
          {loading ? (
            <div className="text-center text-gray-500 dark:text-gray-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-gray-500 dark:text-gray-400">No fields</div>
          ) : (
            filtered.map(name => {
              const isUsed = usedNames.has(name);
              const isSelected = selectedNames.has(name);
              return (
                <label key={name} className={`flex items-center justify-between p-2 rounded border ${isSelected ? 'border-blue-500' : 'border-gray-200 dark:border-gray-700'}`}>
                  <div className="flex items-center space-x-2">
                    <input type="checkbox" disabled={isUsed} checked={isSelected} onChange={() => onToggle(name)} />
                    <span className={`text-sm ${isUsed ? 'text-gray-400' : 'text-gray-900 dark:text-white'}`}>{name}</span>
                  </div>
                  {isUsed && <span className="text-xs text-gray-400">Already added</span>}
                </label>
              );
            })
          )}
        </div>
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end space-x-2">
          <button onClick={onCancel} className="px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg">Cancel</button>
          <button onClick={onConfirm} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg">Add Selected</button>
        </div>
      </div>
    </div>
  );
};

export default ListingConfigManager;

